import * as React from 'react'
import { X } from '@phosphor-icons/react'
import { HtmlContent } from 'components/atoms/editor/html-content'
import { useApiClient } from '../../utils/use-api-client'
import { RatingScaleLabel, RatingScaleResponse } from './types'

type RatingScalesModalProps = {
  orgSlug: string
  onClose: () => void
}

// Read-only preview of the org's active rating scale, reached from Step 3's
// "View current rating scales". Follows the notification editor's "replace the
// wizard" pattern: NewCycleModal early-returns this instead of stacking a second
// overlay, so all wizard state is preserved behind it. Labels (names, colours,
// descriptions) are org config, so they're fetched rather than hard-coded.
export const RatingScalesModal: React.FC<RatingScalesModalProps> = ({
  orgSlug,
  onClose,
}) => {
  const apiClient = useApiClient()
  const [labels, setLabels] = React.useState<RatingScaleLabel[]>([])
  const [loading, setLoading] = React.useState(true)
  const [loadError, setLoadError] = React.useState<string | null>(null)

  React.useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [onClose])

  React.useEffect(() => {
    let cancelled = false

    const loadScale = async () => {
      setLoading(true)
      setLoadError(null)

      try {
        const response = await apiClient.get<RatingScaleResponse>(
          `/api/v1/internal/orgs/${orgSlug}/rating_scale`
        )
        if (cancelled) return
        setLabels(response?.data?.labels ?? [])
      } catch (err: unknown) {
        if (cancelled) return
        const message =
          err instanceof Error ? err.message : 'Failed to load rating scales.'
        setLoadError(message)
      } finally {
        if (!cancelled) setLoading(false)
      }
    }

    loadScale()

    return () => {
      cancelled = true
    }
  }, [apiClient, orgSlug])

  let content: React.ReactNode
  if (loading) {
    content = (
      <p className="text-[15px] text-[#5E6580]">Loading rating scales…</p>
    )
  } else if (loadError) {
    content = <p className="text-[15px] text-[#E42309]">{loadError}</p>
  } else if (labels.length === 0) {
    content = (
      <p className="text-[15px] text-[#5E6580]">
        No rating scale is configured for this organization yet.
      </p>
    )
  } else {
    content = (
      <div className="flex flex-col gap-4">
        {labels.map((label, index) => (
          <div
            key={label.label_slug || `${label.scale_value}-${index}`}
            className="rounded-xl border border-[#E7EAF4] p-5"
          >
            <div className="flex items-start justify-between gap-4">
              <p className="text-[16px] text-[#5E6580]">Scale {index + 1}</p>
              <span className="inline-flex items-center gap-2 rounded-lg border border-[#E7EAF4] px-3 py-1.5 text-[15px] font-medium text-[#1D264A]">
                <span
                  className="h-2.5 w-2.5 shrink-0 rounded-full"
                  style={{ backgroundColor: label.color }}
                />
                {label.label_name}
              </span>
            </div>

            <p className="mt-3 text-[18px] font-semibold text-[#1D264A]">
              {label.label_name}
            </p>
            {label.description && (
              <HtmlContent className="mt-1 text-[16px] text-[#5E6580]">
                {label.description}
              </HtmlContent>
            )}
          </div>
        ))}
      </div>
    )
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-[rgba(29,38,74,0.22)] px-4 py-4">
      <div className="flex h-[689px] max-h-full w-full max-w-[628px] flex-col rounded-lg bg-white p-5 shadow-[0_18px_44px_rgba(29,38,74,0.18)] outline outline-1 outline-offset-[-1px] outline-gray-200">
        <div className="flex items-start justify-between gap-4">
          <h2 className="font-talent text-[27px] font-medium leading-[32px] text-[#1D264A]">
            Current rating scales
          </h2>

          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="inline-flex h-[40px] w-[40px] shrink-0 items-center justify-center rounded-full bg-[#EFF1F8] text-[#5E6580] hover:bg-[#E3E8F1]"
          >
            <X size={20} />
          </button>
        </div>

        <div className="my-4 h-px w-full bg-[#E7EAF4]" />

        <div className="-mx-5 min-h-0 flex-1 overflow-y-auto px-5">
          {content}
        </div>
      </div>
    </div>
  )
}
